'use client';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import Link from 'next/link';
import MoneziteLogo from '@/components/ui/MoneziteLogo';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { LogOut, User, LayoutDashboard, Bell, Lightbulb, DollarSign, MessageCircle, CheckCheck, ShieldCheck, Menu, X, Wallet } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface Notification {
  id: string;
  type: string;
  title: string;
  body: string;
  is_read: boolean;
  created_at: string;
  related_problem_id: string | null;
  related_solution_id: string | null;
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function notifIcon(type: string) {
  if (type === 'new_solution') return <Lightbulb size={14} className="text-success shrink-0 mt-0.5" />;
  if (type === 'new_offer' || type === 'funding_offer') return <DollarSign size={14} className="text-primary shrink-0 mt-0.5" />;
  return <MessageCircle size={14} className="text-warning shrink-0 mt-0.5" />;
}

export default function PublicNav() {
  const [exploreOpen, setExploreOpen] = useState(false);
  const [mobileExploreOpen, setMobileExploreOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifLoading, setNotifLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [mounted, setMounted] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileDropdownRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);
  const { user, signOut, loading } = useAuth();
  const router = useRouter();
  const supabase = createClient();

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    setNotifLoading(true);
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('id, type, title, body, is_read, created_at, related_problem_id, related_solution_id')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20);

      if (!error && data) {
        setNotifications(data);
        setUnreadCount(data.filter((n) => !n.is_read).length);
      }
    } finally {
      setNotifLoading(false);
    }
  }, [user, supabase]);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (user) {
      fetchNotifications();
      supabase
        .from('user_profiles')
        .select('is_admin')
        .eq('id', user.id)
        .single()
        .then(({ data }) => {
          // Hardcoded bypass: admin@monezite.com always has admin access
          setIsAdmin(user.email === 'admin@monezite.com' || data?.is_admin === true);
        });
      const channel = supabase
        .channel('notifications-nav')
        .on(
          'postgres_changes',
          { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
          () => { fetchNotifications(); }
        )
        .subscribe();
      return () => { supabase.removeChannel(channel); };
    }
  }, [user, fetchNotifications, supabase]);

  async function markAllRead() {
    if (!user || unreadCount === 0) return;
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('user_id', user.id)
      .eq('is_read', false);
    if (!error) {
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    }
  }

  async function markOneRead(id: string) {
    await supabase.from('notifications').update({ is_read: true }).eq('id', id);
    setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, is_read: true } : n));
    setUnreadCount((prev) => Math.max(0, prev - 1));
  }

  function notifLink(n: Notification): string {
    if (n.related_solution_id) return `/solution/${n.related_solution_id}`;
    if (n.related_problem_id) return `/problem/${n.related_problem_id}`;
    return '/dashboard';
  }

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setExploreOpen(false);
      }
      if (mobileDropdownRef.current && !mobileDropdownRef.current.contains(e.target as Node)) {
        setMobileExploreOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [router]);

  async function handleSignOut() {
    try {
      await signOut();
      toast.success('Signed out successfully');
      router.push('/');
      router.refresh();
    } catch {
      toast.error('Failed to sign out');
    }
    setUserMenuOpen(false);
    setMobileMenuOpen(false);
  }

  const displayName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Account';
  const initials = displayName.slice(0, 2).toUpperCase();

  return (
    <header className="sticky top-0 z-50 bg-card border-b border-border card-shadow">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 flex items-center justify-between h-16">
        {/* Logo */}
        <Link href="/" className="flex items-center shrink-0">
          <MoneziteLogo height={44} />
        </Link>

        {/* Mobile: Home + Explore links (always visible in top bar) */}
        <div className="flex md:hidden items-center gap-0.5 ml-2">
          <Link
            href="/"
            className="px-2.5 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors duration-150"
          >
            Home
          </Link>
          <div className="relative" ref={mobileDropdownRef}>
            <button
              suppressHydrationWarning
              onClick={() => setMobileExploreOpen((prev) => !prev)}
              className="flex items-center gap-0.5 px-2.5 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors duration-150"
            >
              Explore
              <svg
                className={`w-3.5 h-3.5 transition-transform duration-200 ${mobileExploreOpen ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {mobileExploreOpen && (
              <div className="absolute left-0 top-full mt-1 w-48 bg-card border border-border rounded-xl shadow-lg py-1 z-50">
                <Link
                  href="/problem-solution-board?tab=problems"
                  onClick={() => { setMobileExploreOpen(false); }}
                  className="flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-150"
                >
                  <span className="w-2 h-2 rounded-full bg-warning shrink-0" />
                  Problems
                </Link>
                <Link
                  href="/problem-solution-board?tab=solutions"
                  onClick={() => { setMobileExploreOpen(false); }}
                  className="flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-150"
                >
                  <span className="w-2 h-2 rounded-full bg-success shrink-0" />
                  Solutions
                </Link>
                <Link
                  href="/problem-solution-board?tab=funded"
                  onClick={() => { setMobileExploreOpen(false); }}
                  className="flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-150"
                >
                  <span className="w-2 h-2 rounded-full bg-primary shrink-0" />
                  Funding
                </Link>
                <Link
                  href="/workers"
                  onClick={() => { setMobileExploreOpen(false); }}
                  className="flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-150"
                >
                  <span className="w-2 h-2 rounded-full bg-violet-500 shrink-0" />
                  Get Hired or Hire
                </Link>
                <Link
                  href="/marketplace"
                  onClick={() => { setMobileExploreOpen(false); }}
                  className="flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-150"
                >
                  <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                  Marketplace
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Desktop Nav links */}
        <nav className="hidden md:flex items-center gap-1">
          <Link
            href="/"
            className="px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors duration-150"
          >
            Home
          </Link>

          {/* Explore Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              suppressHydrationWarning
              onClick={() => setExploreOpen((prev) => !prev)}
              className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors duration-150"
            >
              Explore
              <svg
                className={`w-4 h-4 transition-transform duration-200 ${exploreOpen ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {exploreOpen && (
              <div className="absolute left-0 top-full mt-1 w-48 bg-card border border-border rounded-xl shadow-lg py-1 z-50">
                <Link
                  href="/problem-solution-board?tab=problems"
                  onClick={() => setExploreOpen(false)}
                  className="flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-150"
                >
                  <span className="w-2 h-2 rounded-full bg-warning shrink-0" />
                  Problems
                </Link>
                <Link
                  href="/problem-solution-board?tab=solutions"
                  onClick={() => setExploreOpen(false)}
                  className="flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-150"
                >
                  <span className="w-2 h-2 rounded-full bg-success shrink-0" />
                  Solutions
                </Link>
                <Link
                  href="/problem-solution-board?tab=funded"
                  onClick={() => setExploreOpen(false)}
                  className="flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-150"
                >
                  <span className="w-2 h-2 rounded-full bg-primary shrink-0" />
                  Funding
                </Link>
                <Link
                  href="/workers"
                  onClick={() => setExploreOpen(false)}
                  className="flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-150"
                >
                  <span className="w-2 h-2 rounded-full bg-violet-500 shrink-0" />
                  Get Hired or Hire
                </Link>
                <Link
                  href="/marketplace"
                  onClick={() => setExploreOpen(false)}
                  className="flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-150"
                >
                  <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                  Marketplace
                </Link>
              </div>
            )}
          </div>
        </nav>

        {/* Desktop Actions */}
        <div className="hidden md:flex items-center gap-2">
          {!mounted || loading ? (
            <div className="flex items-center gap-2">
              <Link
                href="/sign-up-login-screen?mode=login"
                className="px-3 py-2 text-sm font-semibold text-primary hover:bg-secondary rounded-lg transition-colors duration-150 whitespace-nowrap"
              >
                Log In
              </Link>
              <Link
                href="/sign-up-login-screen?mode=signup"
                className="px-3 py-2 text-sm font-semibold text-primary-foreground bg-primary hover:bg-cyan-600 rounded-lg transition-all duration-150 active:scale-95"
              >
                Get Started
              </Link>
            </div>
          ) : user ? (
            <>
              {/* Notification Bell */}
              <div className="relative" ref={notifRef}>
                <button
                  suppressHydrationWarning
                  onClick={() => { setNotifOpen((prev) => !prev); setUserMenuOpen(false); }}
                  className="relative p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-150"
                  aria-label="Notifications"
                >
                  <Bell size={18} />
                  {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 min-w-[16px] h-4 px-0.5 flex items-center justify-center rounded-full bg-danger text-[10px] font-bold text-white leading-none">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>

                {notifOpen && (
                  <div className="absolute right-0 top-full mt-1 w-80 bg-card border border-border rounded-xl shadow-xl z-50 overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                      <span className="text-sm font-semibold text-foreground">Notifications</span>
                      {unreadCount > 0 && (
                        <button
                          suppressHydrationWarning
                          onClick={markAllRead}
                          className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 font-medium transition-colors"
                        >
                          <CheckCheck size={12} />
                          Mark all read
                        </button>
                      )}
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {notifLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        </div>
                      ) : notifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-10 gap-2 text-muted-foreground">
                          <Bell size={24} className="opacity-30" />
                          <p className="text-sm">No notifications yet</p>
                        </div>
                      ) : (
                        notifications.map((n) => (
                          <Link
                            key={n.id}
                            href={notifLink(n)}
                            onClick={() => { if (!n.is_read) markOneRead(n.id); setNotifOpen(false); }}
                            className={`flex items-start gap-3 px-4 py-3 hover:bg-muted transition-colors duration-150 border-b border-border/50 last:border-0 ${!n.is_read ? 'bg-primary/5' : ''}`}
                          >
                            <div className="mt-0.5">{notifIcon(n.type)}</div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-xs leading-snug truncate ${!n.is_read ? 'font-semibold text-foreground' : 'font-medium text-muted-foreground'}`}>
                                {n.title}
                              </p>
                              {n.body && (
                                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2 leading-snug">{n.body}</p>
                              )}
                              <p className="text-[10px] text-muted-foreground/60 mt-1">{timeAgo(n.created_at)}</p>
                            </div>
                            {!n.is_read && (
                              <span className="w-2 h-2 rounded-full bg-primary shrink-0 mt-1.5" />
                            )}
                          </Link>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* User Menu */}
              <div className="relative" ref={userMenuRef}>
                <button
                  suppressHydrationWarning
                  onClick={() => { setUserMenuOpen((prev) => !prev); setNotifOpen(false); }}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-muted transition-colors duration-150"
                >
                  <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center shrink-0">
                    <span className="text-xs font-bold text-primary-foreground">{initials}</span>
                  </div>
                  <span className="text-sm font-medium text-foreground hidden lg:block max-w-[120px] truncate">
                    {displayName}
                  </span>
                  <svg
                    className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${userMenuOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-1 w-52 bg-card border border-border rounded-xl shadow-lg py-1 z-50">
                    <div className="px-4 py-2.5 border-b border-border">
                      <p className="text-xs font-semibold text-foreground truncate">{displayName}</p>
                      <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                    </div>
                    <Link
                      href="/profile"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-150"
                    >
                      <User size={14} />
                      My Profile
                    </Link>
                    <Link
                      href="/dashboard"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-150"
                    >
                      <LayoutDashboard size={14} />
                      My Dashboard
                    </Link>
                    <Link
                      href="/wallet"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-150"
                    >
                      <Wallet size={14} />
                      My Wallet
                    </Link>
                    {isAdmin && (
                      <Link
                        href="/admin"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-primary hover:text-primary/80 hover:bg-primary/5 transition-colors duration-150"
                      >
                        <ShieldCheck size={14} />
                        Admin Dashboard
                      </Link>
                    )}
                    {isAdmin && (
                      <Link
                        href="/admin/wallet"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-primary hover:text-primary/80 hover:bg-primary/5 transition-colors duration-150"
                      >
                        <Wallet size={14} />
                        Wallet Admin
                      </Link>
                    )}
                    <button
                      suppressHydrationWarning
                      onClick={handleSignOut}
                      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-danger hover:bg-danger/5 transition-colors duration-150"
                    >
                      <LogOut size={14} />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <Link
                href="/sign-up-login-screen?mode=login"
                className="px-3 py-2 text-sm font-semibold text-primary hover:bg-secondary rounded-lg transition-colors duration-150 whitespace-nowrap"
              >
                Log In
              </Link>
              <Link
                href="/sign-up-login-screen?mode=signup"
                className="px-3 py-2 text-sm font-semibold text-primary-foreground bg-primary hover:bg-cyan-600 rounded-lg transition-all duration-150 active:scale-95"
              >
                Get Started
              </Link>
            </>
          )}
        </div>

        {/* Mobile right side: notification bell (if logged in) + hamburger */}
        <div className="flex md:hidden items-center gap-1">
          {mounted && !loading && user && (
            <div className="relative" ref={notifRef}>
              <button
                suppressHydrationWarning
                onClick={() => { setNotifOpen((prev) => !prev); }}
                className="relative p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-150"
                aria-label="Notifications"
              >
                <Bell size={18} />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 min-w-[16px] h-4 px-0.5 flex items-center justify-center rounded-full bg-danger text-[10px] font-bold text-white leading-none">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
              {notifOpen && (
                <div className="absolute right-0 top-full mt-1 w-[calc(100vw-2rem)] max-w-sm bg-card border border-border rounded-xl shadow-xl z-50 overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                    <span className="text-sm font-semibold text-foreground">Notifications</span>
                    {unreadCount > 0 && (
                      <button suppressHydrationWarning onClick={markAllRead} className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 font-medium transition-colors">
                        <CheckCheck size={12} />
                        Mark all read
                      </button>
                    )}
                  </div>
                  <div className="max-h-72 overflow-y-auto">
                    {notifLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : notifications.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-10 gap-2 text-muted-foreground">
                        <Bell size={24} className="opacity-30" />
                        <p className="text-sm">No notifications yet</p>
                      </div>
                    ) : (
                      notifications.map((n) => (
                        <Link
                          key={n.id}
                          href={notifLink(n)}
                          onClick={() => { if (!n.is_read) markOneRead(n.id); setNotifOpen(false); }}
                          className={`flex items-start gap-3 px-4 py-3 hover:bg-muted transition-colors duration-150 border-b border-border/50 last:border-0 ${!n.is_read ? 'bg-primary/5' : ''}`}
                        >
                          <div className="mt-0.5">{notifIcon(n.type)}</div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-xs leading-snug truncate ${!n.is_read ? 'font-semibold text-foreground' : 'font-medium text-muted-foreground'}`}>{n.title}</p>
                            {n.body && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2 leading-snug">{n.body}</p>}
                            <p className="text-[10px] text-muted-foreground/60 mt-1">{timeAgo(n.created_at)}</p>
                          </div>
                          {!n.is_read && <span className="w-2 h-2 rounded-full bg-primary shrink-0 mt-1.5" />}
                        </Link>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          <button
            suppressHydrationWarning
            onClick={() => setMobileMenuOpen((prev) => !prev)}
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-150"
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Drawer */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-border bg-card">
          <nav className="px-4 py-3 flex flex-col gap-1">
            {/* Mobile auth / user section */}
            <div className="flex flex-col gap-1">
              {!mounted || loading ? null : user ? (
                <>
                  <div className="flex items-center gap-3 px-3 py-2 mb-1">
                    <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center shrink-0">
                      <span className="text-sm font-bold text-primary-foreground">{initials}</span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">{displayName}</p>
                      <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                    </div>
                  </div>
                  <Link href="/profile" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors">
                    <User size={15} /> My Profile
                  </Link>
                  <Link href="/dashboard" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors">
                    <LayoutDashboard size={15} /> My Dashboard
                  </Link>
                  <Link href="/wallet" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors">
                    <Wallet size={15} /> My Wallet
                  </Link>
                  {isAdmin && (
                    <Link href="/admin" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium text-primary hover:bg-primary/5 rounded-lg transition-colors">
                      <ShieldCheck size={15} /> Admin Dashboard
                    </Link>
                  )}
                  {isAdmin && (
                    <Link href="/admin/wallet" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium text-primary hover:bg-primary/5 rounded-lg transition-colors">
                      <Wallet size={15} /> Wallet Admin
                    </Link>
                  )}
                  <button suppressHydrationWarning onClick={handleSignOut} className="flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium text-danger hover:bg-danger/5 rounded-lg transition-colors">
                    <LogOut size={15} /> Sign Out
                  </button>
                </>
              ) : (
                <div className="flex flex-col gap-2 px-1">
                  <Link href="/sign-up-login-screen?mode=login" onClick={() => setMobileMenuOpen(false)} className="flex items-center justify-center px-4 py-2.5 text-sm font-semibold text-primary border border-primary/30 hover:bg-primary/5 rounded-lg transition-colors">
                    Log In
                  </Link>
                  <Link href="/sign-up-login-screen?mode=signup" onClick={() => setMobileMenuOpen(false)} className="flex items-center justify-center px-4 py-2.5 text-sm font-semibold text-primary-foreground bg-primary hover:bg-cyan-600 rounded-lg transition-colors">
                    Get Started
                  </Link>
                </div>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}